import { memo, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../hooks/useTheme'
import type { CompetitorMetric } from '../../features/metrics/model/contracts'

interface RadarChartProps {
  data: CompetitorMetric[]
}

const CX = 120
const CY = 120
const R = 85
const LEVELS = 4
const CHART_FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"

type SeriesKey = 'ours' | 'competitorA' | 'competitorB'

interface SeriesConfig {
  key: SeriesKey
  label: string
  color: string
  fillOpacity: number
}

function readCssColor(prop: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(prop).trim()
}

function buildSeries(labels: Record<SeriesKey, string>): SeriesConfig[] {
  return [
    {
      key: 'ours',
      label: labels.ours,
      color: readCssColor('--chart-line'),
      fillOpacity: 0.15,
    },
    {
      key: 'competitorA',
      label: labels.competitorA,
      color: readCssColor('--sentiment-negative'),
      fillOpacity: 0.08,
    },
    {
      key: 'competitorB',
      label: labels.competitorB,
      color: readCssColor('--text-3'),
      fillOpacity: 0.06,
    },
  ]
}

function polarToCart(angle: number, radius: number) {
  const rad = (angle - 90) * (Math.PI / 180)
  return { x: CX + radius * Math.cos(rad), y: CY + radius * Math.sin(rad) }
}

export const RadarChart = memo(function RadarChart({ data }: RadarChartProps) {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const id = useId()
  const seriesLabels: Record<SeriesKey, string> = {
    ours: t('analytics.radarSeries.ours', { defaultValue: 'Omni Sight' }),
    competitorA: t('analytics.radarSeries.competitorA', { defaultValue: 'Competitor A' }),
    competitorB: t('analytics.radarSeries.competitorB', { defaultValue: 'Competitor B' }),
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read CSS custom props when theme changes
  const series = useMemo(() => buildSeries(seriesLabels), [seriesLabels, theme])
  const n = data.length

  if (n < 3) return null

  const angleStep = 360 / n

  // Grid levels
  const gridLevels = Array.from({ length: LEVELS }, (_, i) => ((i + 1) / LEVELS) * R)

  // Axis lines + labels
  const axes = data.map((d, i) => {
    const angle = i * angleStep
    const end = polarToCart(angle, R)
    const labelPos = polarToCart(angle, R + 14)
    return { ...d, angle, end, labelPos }
  })

  // Build polygon path for a series
  function seriesPath(seriesKey: SeriesKey): string {
    return (
      data
        .map((d, i) => {
          const val = d[seriesKey] / 100
          const { x, y } = polarToCart(i * angleStep, R * val)
          return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(' ') + ' Z'
    )
  }

  return (
    <div>
      <svg
        viewBox="0 0 240 240"
        className="mx-auto w-full max-w-[240px]"
        role="img"
        aria-label={t('analytics.radarChart.aria', {
          defaultValue: 'Radar chart comparing brand health across {{count}} dimensions',
          count: data.length,
        })}
      >
        <title>
          {t('analytics.radarChart.title', {
            defaultValue: 'Brand health comparison chart',
          })}
        </title>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`${id}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={s.fillOpacity * 2} />
              <stop offset="100%" stopColor={s.color} stopOpacity={s.fillOpacity} />
            </linearGradient>
          ))}
        </defs>

        {/* Grid levels */}
        {gridLevels.map((r) => (
          <polygon
            key={r}
            points={Array.from({ length: n }, (_, i) => {
              const { x, y } = polarToCart(i * angleStep, r)
              return `${x.toFixed(1)},${y.toFixed(1)}`
            }).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-border"
          />
        ))}

        {/* Axis lines */}
        {axes.map((a) => (
          <line
            key={a.dimension}
            x1={CX}
            y1={CY}
            x2={a.end.x}
            y2={a.end.y}
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-border"
          />
        ))}

        {/* Series polygons — drawn back-to-front */}
        {[...series].reverse().map((s) => (
          <path
            key={s.key}
            d={seriesPath(s.key)}
            fill={`url(#${id}-${s.key})`}
            stroke={s.color}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        ))}

        {/* Data points for primary series */}
        {data.map((d, i) => {
          const val = d.ours / 100
          const { x, y } = polarToCart(i * angleStep, R * val)
          return <circle key={i} cx={x} cy={y} r="3" fill={series[0].color} />
        })}

        {/* Axis labels */}
        {axes.map((a) => (
          <text
            key={a.dimension}
            x={a.labelPos.x}
            y={a.labelPos.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-muted-foreground"
            fontSize="9"
            fontFamily={CHART_FONT}
            fontWeight="500"
          >
            {a.dimension}
          </text>
        ))}
      </svg>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {series.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5 text-[11px]">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-muted-foreground">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
})
